/**
 * MCP client config writer
 *
 * Auto-writes / merges the `powerautomate` server entry into the config file of
 * common MCP clients so users never have to hand-edit JSON. Existing servers and
 * unrelated keys are preserved. The merge logic is a pure function
 * (`mergeServerEntry`) so it can be unit-tested without touching the filesystem.
 *
 * Claude Code is handled separately via its official CLI (`claude mcp add`)
 * because its config file (`~/.claude.json`) holds broader client state that we
 * must not risk corrupting with a hand-merge.
 */

import { execFile } from "node:child_process";
import {
  existsSync,
  readFileSync,
  writeFileSync,
  mkdirSync,
  copyFileSync,
} from "node:fs";
import { dirname, join } from "node:path";
import { homedir } from "node:os";
import { env, platform } from "node:process";

/** The key our server is registered under in every client config. */
export const SERVER_NAME = "powerautomate";

/** Clients whose JSON config we can safely write/merge directly. */
export type JsonClientId = "claude" | "cursor" | "vscode" | "gemini" | "windsurf";
/** All clients we know how to connect (includes the CLI-driven Claude Code and OpenAI Codex). */
export type ClientId = JsonClientId | "claude-code" | "codex";

export interface JsonClientInfo {
  id: JsonClientId;
  label: string;
  /** Absolute path to the client's config file. */
  path: string;
  /** Top-level key the client expects its servers under. */
  key: "mcpServers" | "servers";
  /** VS Code requires an explicit `"type": "stdio"` on each entry. */
  includeType: boolean;
  /** Message telling the user how to make the client pick up the change. */
  restartHint: string;
}

/** Human-facing labels for menus and messages. */
export const CLIENT_LABELS: Record<ClientId, string> = {
  claude: "Claude Desktop",
  "claude-code": "Claude Code (CLI)",
  codex: "OpenAI Codex (ChatGPT CLI)",
  cursor: "Cursor",
  vscode: "VS Code (GitHub Copilot)",
  gemini: "Google Gemini CLI",
  windsurf: "Windsurf",
};

/** Accepted aliases for the --client flag, mapped to a canonical ClientId. */
const CLIENT_ALIASES: Record<string, ClientId> = {
  claude: "claude",
  "claude-desktop": "claude",
  desktop: "claude",
  "claude-code": "claude-code",
  claudecode: "claude-code",
  code: "claude-code",
  codex: "codex",
  "openai-codex": "codex",
  "codex-cli": "codex",
  chatgpt: "codex",
  cursor: "cursor",
  vscode: "vscode",
  "vs-code": "vscode",
  copilot: "vscode",
  gemini: "gemini",
  windsurf: "windsurf",
};

/**
 * Resolve a user-supplied client name (case-insensitive, alias-aware) to a
 * canonical ClientId, or null if unrecognized.
 */
export function normalizeClientId(raw: string): ClientId | null {
  return CLIENT_ALIASES[raw.trim().toLowerCase()] ?? null;
}

/**
 * Resolve the JSON config file path + shape for a directly-writable client on
 * the current OS. Returns null for clients we don't write directly (claude-code).
 */
export function getJsonClientInfo(id: ClientId): JsonClientInfo | null {
  const home = homedir();
  const isWin = platform === "win32";
  const isMac = platform === "darwin";
  const appData = env["APPDATA"] ?? join(home, "AppData", "Roaming");
  const xdg = env["XDG_CONFIG_HOME"] ?? join(home, ".config");

  switch (id) {
    case "claude": {
      const path = isWin
        ? join(appData, "Claude", "claude_desktop_config.json")
        : isMac
          ? join(home, "Library", "Application Support", "Claude", "claude_desktop_config.json")
          : join(xdg, "Claude", "claude_desktop_config.json");
      return {
        id,
        label: CLIENT_LABELS[id],
        path,
        key: "mcpServers",
        includeType: false,
        restartHint: "Quit and reopen Claude Desktop. The Power Automate tools appear automatically.",
      };
    }
    case "cursor":
      return {
        id,
        label: CLIENT_LABELS[id],
        path: join(home, ".cursor", "mcp.json"),
        key: "mcpServers",
        includeType: false,
        restartHint: "Restart Cursor to pick up the new server.",
      };
    case "gemini":
      return {
        id,
        label: CLIENT_LABELS[id],
        path: join(home, ".gemini", "settings.json"),
        key: "mcpServers",
        includeType: false,
        restartHint: "Restart the Gemini CLI session.",
      };
    case "windsurf":
      return {
        id,
        label: CLIENT_LABELS[id],
        path: join(home, ".codeium", "windsurf", "mcp_config.json"),
        key: "mcpServers",
        includeType: false,
        restartHint: "Restart Windsurf, then refresh MCP servers in settings.",
      };
    case "vscode": {
      // VS Code user-level MCP config (Command Palette → "MCP: Open User Configuration").
      const path = isWin
        ? join(appData, "Code", "User", "mcp.json")
        : isMac
          ? join(home, "Library", "Application Support", "Code", "User", "mcp.json")
          : join(xdg, "Code", "User", "mcp.json");
      return {
        id,
        label: CLIENT_LABELS[id],
        path,
        key: "servers",
        includeType: true,
        restartHint: "Reload VS Code (Command Palette → \"Developer: Reload Window\").",
      };
    }
    default:
      return null; // claude-code and codex are handled via their own CLIs
  }
}

/** The command an MCP client should run to launch this server. */
export interface ServerCommand {
  command: string;
  args?: string[];
}

/**
 * Build the launch command. `useNpx` produces a zero-global-install entry that
 * fetches the package on demand; otherwise it relies on the global binary.
 *
 * On Windows the global bin (`powerautomate-mcp.cmd`) and `npx` (`npx.cmd`) are
 * shell shims that most MCP clients can't spawn directly (they launch with no
 * shell), so we wrap them in `cmd /c` — the documented Windows form for MCP
 * stdio servers. `platformOverride` exists for tests.
 */
export function getServerCommand(useNpx: boolean, platformOverride?: NodeJS.Platform): ServerCommand {
  const isWin = (platformOverride ?? platform) === "win32";
  if (useNpx) {
    return isWin
      ? { command: "cmd", args: ["/c", "npx", "-y", "powerautomate-mcp@latest"] }
      : { command: "npx", args: ["-y", "powerautomate-mcp@latest"] };
  }
  return isWin
    ? { command: "cmd", args: ["/c", "powerautomate-mcp"] }
    : { command: "powerautomate-mcp" };
}

export type MergeAction = "created" | "updated" | "unchanged";

/**
 * Pure merge: insert/replace `serverName` under `key` in a parsed client config,
 * preserving every other key and server. Returns a NEW object plus what changed.
 */
export function mergeServerEntry(
  existing: Record<string, unknown> | null,
  key: "mcpServers" | "servers",
  serverName: string,
  entry: Record<string, unknown>
): { result: Record<string, unknown>; action: MergeAction } {
  const base: Record<string, unknown> =
    existing && typeof existing === "object" && !Array.isArray(existing)
      ? { ...existing }
      : {};

  const current = base[key];
  const servers: Record<string, unknown> =
    current && typeof current === "object" && !Array.isArray(current)
      ? { ...(current as Record<string, unknown>) }
      : {};

  const prev = servers[serverName];
  const action: MergeAction =
    prev === undefined
      ? "created"
      : JSON.stringify(prev) === JSON.stringify(entry)
        ? "unchanged"
        : "updated";

  servers[serverName] = entry;
  base[key] = servers;
  return { result: base, action };
}

/** Build the server entry object for a given client shape. */
export function buildServerEntry(info: JsonClientInfo, cmd: ServerCommand): Record<string, unknown> {
  const entry: Record<string, unknown> = info.includeType
    ? { type: "stdio", command: cmd.command }
    : { command: cmd.command };
  if (cmd.args) entry["args"] = cmd.args;
  return entry;
}

export interface WriteResult {
  ok: boolean;
  clientId: ClientId;
  label: string;
  path?: string;
  action: MergeAction | "error" | "manual";
  backupPath?: string;
  /** Present when action === "manual" (claude-code without the CLI on PATH). */
  manualCommand?: string;
  error?: string;
}

/**
 * Read + parse an existing client config, tolerating an absent or empty file.
 * On malformed JSON, returns `{ corrupt: true }` so the caller can back it up
 * rather than silently discarding the user's other servers.
 */
function readExistingConfig(path: string): { data: Record<string, unknown> | null; corrupt: boolean } {
  if (!existsSync(path)) return { data: null, corrupt: false };
  try {
    const raw = readFileSync(path, "utf-8").trim();
    if (!raw) return { data: null, corrupt: false };
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return { data: parsed as Record<string, unknown>, corrupt: false };
    }
    return { data: null, corrupt: true };
  } catch {
    return { data: null, corrupt: true };
  }
}

/**
 * Write/merge the powerautomate server into a directly-writable client's config.
 * Creates parent directories as needed. If the existing file is malformed JSON,
 * it is backed up to `<file>.bak` before a fresh config is written.
 */
export function writeJsonClientConfig(
  info: JsonClientInfo,
  opts: { useNpx?: boolean } = {}
): WriteResult {
  const cmd = getServerCommand(!!opts.useNpx);
  const entry = buildServerEntry(info, cmd);

  let backupPath: string | undefined;
  const { data: existing, corrupt } = readExistingConfig(info.path);

  if (corrupt) {
    backupPath = `${info.path}.bak`;
    try {
      copyFileSync(info.path, backupPath);
    } catch {
      backupPath = undefined; // best-effort backup
    }
  }

  const { result, action } = mergeServerEntry(corrupt ? null : existing, info.key, SERVER_NAME, entry);

  // Nothing to do — already wired identically and the file was healthy.
  if (action === "unchanged" && !backupPath) {
    return { ok: true, clientId: info.id, label: info.label, path: info.path, action: "unchanged" };
  }

  try {
    mkdirSync(dirname(info.path), { recursive: true });
    writeFileSync(info.path, `${JSON.stringify(result, null, 2)}\n`, { encoding: "utf-8" });
  } catch (err) {
    return {
      ok: false,
      clientId: info.id,
      label: info.label,
      path: info.path,
      action: "error",
      error: err instanceof Error ? err.message : String(err),
    };
  }

  return { ok: true, clientId: info.id, label: info.label, path: info.path, action, backupPath };
}

/** Run a binary safely (execFile, not exec) and resolve true on exit 0. */
function tryExecFile(cmd: string, args: string[]): Promise<{ ok: boolean; output: string }> {
  return new Promise((resolve) => {
    const isWin = platform === "win32";
    const bin = isWin ? "cmd.exe" : cmd;
    const binArgs = isWin ? ["/c", cmd, ...args] : args;
    execFile(bin, binArgs, { timeout: 30_000 }, (error, stdout, stderr) => {
      if (error) resolve({ ok: false, output: (stderr || error.message || "").trim() });
      else resolve({ ok: true, output: (stdout || "").trim() });
    });
  });
}

/**
 * Connect Claude Code via its official CLI. Falls back to returning the exact
 * command for the user to run if the `claude` binary isn't on PATH.
 */
export async function connectClaudeCode(opts: { useNpx?: boolean } = {}): Promise<WriteResult> {
  const cmd = getServerCommand(!!opts.useNpx);
  const serverArgs = cmd.args ? [cmd.command, ...cmd.args] : [cmd.command];
  // `-s user` makes the server available across all projects. Without it the
  // CLI defaults to `local` scope (bound to the current directory), which would
  // silently make the tools absent everywhere else.
  const addArgs = ["mcp", "add", "-s", "user", SERVER_NAME, "--", ...serverArgs];
  const manualCommand = `claude ${addArgs.join(" ")}`;
  const label = CLIENT_LABELS["claude-code"];

  const present = await tryExecFile("claude", ["--version"]);
  if (!present.ok) {
    return {
      ok: false,
      clientId: "claude-code",
      label,
      action: "manual",
      manualCommand,
      error: "Claude Code CLI (`claude`) not found on PATH.",
    };
  }

  // Already registered? `claude mcp add` errors on a duplicate, so check first
  // and report it as a no-op instead of handing back a command that re-fails.
  const existing = await tryExecFile("claude", ["mcp", "get", SERVER_NAME]);
  if (existing.ok) {
    return { ok: true, clientId: "claude-code", label, action: "unchanged" };
  }

  const result = await tryExecFile("claude", addArgs);
  if (!result.ok) {
    return {
      ok: false,
      clientId: "claude-code",
      label,
      action: "manual",
      manualCommand,
      error: result.output || "claude mcp add failed",
    };
  }
  return { ok: true, clientId: "claude-code", label, action: "created" };
}

/**
 * Locate the Codex CLI binary. The standalone `codex` command still exists,
 * but since the ChatGPT rebrand the CLI commonly ships INSIDE the ChatGPT
 * desktop app (macOS: ChatGPT.app/Contents/Resources/codex) without being on
 * PATH — a machine can have a fully working Codex with no `codex` command.
 * Returns the invocable binary (name or absolute path), or null.
 */
export async function resolveCodexBinary(): Promise<string | null> {
  const onPath = await tryExecFile("codex", ["--version"]);
  if (onPath.ok) return "codex";

  if (platform === "darwin") {
    const bundled = "/Applications/ChatGPT.app/Contents/Resources/codex";
    if (existsSync(bundled)) {
      const works = await tryExecFile(bundled, ["--version"]);
      if (works.ok) return bundled;
    }
  }
  return null;
}

/**
 * Connect OpenAI Codex (the ChatGPT coding CLI) via its official CLI
 * (`codex mcp add`). Codex stores MCP servers in ~/.codex/config.toml; going
 * through the CLI avoids hand-editing TOML and inherits Codex's own
 * validation. Falls back to returning the exact command for the user to run
 * if no usable binary is found.
 */
export async function connectCodex(opts: { useNpx?: boolean } = {}): Promise<WriteResult> {
  const cmd = getServerCommand(!!opts.useNpx);
  const serverArgs = cmd.args ? [cmd.command, ...cmd.args] : [cmd.command];
  const addArgs = ["mcp", "add", SERVER_NAME, "--", ...serverArgs];
  const label = CLIENT_LABELS["codex"];

  const bin = await resolveCodexBinary();
  if (!bin) {
    return {
      ok: false,
      clientId: "codex",
      label,
      action: "manual",
      manualCommand: `codex ${addArgs.join(" ")}`,
      error: "Codex CLI not found — not on PATH and no ChatGPT app install detected.",
    };
  }

  // Already registered? Report a no-op instead of re-adding.
  const existing = await tryExecFile(bin, ["mcp", "get", SERVER_NAME]);
  if (existing.ok) {
    return { ok: true, clientId: "codex", label, action: "unchanged" };
  }

  const result = await tryExecFile(bin, addArgs);
  if (!result.ok) {
    return {
      ok: false,
      clientId: "codex",
      label,
      action: "manual",
      manualCommand: `${bin} ${addArgs.join(" ")}`,
      error: result.output || "codex mcp add failed",
    };
  }
  return { ok: true, clientId: "codex", label, action: "created" };
}

/**
 * High-level connect: routes to the JSON writer or a client's own CLI.
 */
export async function connectClient(id: ClientId, opts: { useNpx?: boolean } = {}): Promise<WriteResult> {
  if (id === "claude-code") return connectClaudeCode(opts);
  if (id === "codex") return connectCodex(opts);
  const info = getJsonClientInfo(id);
  if (!info) {
    return { ok: false, clientId: id, label: CLIENT_LABELS[id] ?? id, action: "error", error: "Unknown client" };
  }
  return writeJsonClientConfig(info, opts);
}

export interface ClientDetection {
  id: ClientId;
  label: string;
  /** Config file exists on disk (n/a for claude-code → undefined). */
  present?: boolean;
  /** Our server is already registered in that config. */
  wired: boolean;
  path?: string;
}

/**
 * Scan known client config files and report which ones already have the
 * powerautomate server wired up. Used by `--doctor`. Read-only.
 */
export function detectClientConfigs(): ClientDetection[] {
  const jsonIds: JsonClientId[] = ["claude", "cursor", "vscode", "gemini", "windsurf"];
  const out: ClientDetection[] = [];

  for (const id of jsonIds) {
    const info = getJsonClientInfo(id);
    if (!info) continue;
    const { data } = readExistingConfig(info.path);
    const present = existsSync(info.path);
    const servers = data?.[info.key];
    const wired =
      !!servers &&
      typeof servers === "object" &&
      !Array.isArray(servers) &&
      SERVER_NAME in (servers as Record<string, unknown>);
    out.push({ id, label: info.label, present, wired, path: info.path });
  }
  return out;
}

/**
 * Detect Claude Code separately (it isn't a JSON file we read — we ask its CLI).
 * `claude mcp get <name>` exits 0 when the server is registered. Returns
 * present=false when the `claude` binary isn't installed. Read-only.
 */
export async function detectClaudeCode(): Promise<ClientDetection> {
  const label = CLIENT_LABELS["claude-code"];
  const hasCli = await tryExecFile("claude", ["--version"]);
  if (!hasCli.ok) {
    return { id: "claude-code", label, present: false, wired: false };
  }
  const got = await tryExecFile("claude", ["mcp", "get", SERVER_NAME]);
  return { id: "claude-code", label, present: true, wired: got.ok };
}

/**
 * Detect OpenAI Codex the same way — via its CLI. `codex mcp get <name>`
 * exits 0 when the server is registered. Returns present=false when the
 * `codex` binary isn't installed. Read-only.
 */
export async function detectCodex(): Promise<ClientDetection> {
  const label = CLIENT_LABELS["codex"];
  const bin = await resolveCodexBinary();
  if (!bin) {
    return { id: "codex", label, present: false, wired: false };
  }
  const got = await tryExecFile(bin, ["mcp", "get", SERVER_NAME]);
  return { id: "codex", label, present: true, wired: got.ok };
}
